import { useMemo, useCallback, useEffect, useState } from 'react';

import { type OrgsSchools, useOrgsMapFromOrgs } from 'app/dataProviders/org/OrgsDataProvider';
import { Org } from 'app/dataTypes/org';


const defaultSelection: string[] = [];

type SelectorProps = {
  orgs: Map<string, Org>,
  selection: Array<string>,
  defaultSelection: Array<string>,
  onChange: (values: Array<string>) => void,
}

export default function useOrgsSelector(orgsState: OrgsSchools): [SelectorProps, () => void] {
  const orgs = useOrgsMapFromOrgs(orgsState);
  const [selection, setSelection] = useState<Array<string>>(defaultSelection);

  const actualOrgIDs = useMemo(() => {
    return new Set(Array.from(orgs.keys()));
  }, [orgs]);

  const normalizeSelection = useCallback((values: Array<string>) => {
    return values.filter(id => actualOrgIDs.has(id));
  }, [actualOrgIDs]);

  const handleChange = useCallback((values: Array<string>) => {
    setSelection(normalizeSelection(values));
  }, [normalizeSelection]);

  const resetSelection = useCallback(() => {
    setSelection(defaultSelection);
  }, []);

  useEffect(() => {
    setSelection((currentSelection) => {
      const normalizedSelection = normalizeSelection(currentSelection);
      return normalizedSelection.length === currentSelection.length
        ? currentSelection
        : normalizedSelection;
    });
  }, [normalizeSelection]);

  const selectorProps = useMemo<SelectorProps>(() => ({
    orgs,
    selection,
    defaultSelection,
    onChange: handleChange,
  }), [handleChange, orgs, selection]);

  return [selectorProps, resetSelection];
}
